
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loader2, Brain, Sparkles, PenTool, BarChart3, Globe } from "lucide-react";
import { useAdaptiveNotes } from "@/hooks/useAdaptiveNotes";
import { categories, getSubjects } from "@/data/questions";
import { toast } from "sonner";
import { AdaptiveNoteOptions } from "@/types/notes";
import { useIsMobile } from "@/hooks/use-mobile";
import LanguageSelector from "@/components/flashcards/LanguageSelector";

interface AdaptiveNotesGeneratorProps {
  onNoteGenerated?: (noteId: string) => void;
}

const AdaptiveNotesGenerator: React.FC<AdaptiveNotesGeneratorProps> = ({ 
  onNoteGenerated 
}) => {
  const [category, setCategory] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const [difficulty, setDifficulty] = useState<string>("medium");
  const [includeVisuals, setIncludeVisuals] = useState(true);
  const [includeExamples, setIncludeExamples] = useState(true);
  const [availableSubjects, setAvailableSubjects] = useState<string[]>([]);
  const [language, setLanguage] = useState<string>("en"); // Default to English
  
  const { isGenerating, generateAdaptiveNotes } = useAdaptiveNotes();
  const { isMobile, screenSize } = useIsMobile();

  // Update available subjects when category changes
  React.useEffect(() => {
    if (category) {
      const subjects = getSubjects(category);
      setAvailableSubjects(subjects);
      setSubject(""); // Reset subject when category changes
    } else {
      setAvailableSubjects([]);
    }
  }, [category]);

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
  };

  const handleGenerate = async () => {
    if (!category || !subject) {
      toast.error("Please select both category and subject");
      return;
    }

    try {
      const options: AdaptiveNoteOptions = {
        difficulty: difficulty as 'easy' | 'medium' | 'hard',
        includeVisuals,
        includeExamples,
        language, // Pass the selected language
      };

      const newNote = await generateAdaptiveNotes(subject, category, options);
      
      if (newNote && onNoteGenerated) {
        onNoteGenerated(newNote.id);
      }
    } catch (error) {
      console.error("Error generating adaptive notes:", error);
      toast.error("Failed to generate adaptive notes");
    }
  };

  const cardPadding = isMobile ? 
    (screenSize === 'xs' ? 'p-3' : 'p-4') : 
    'p-6';
  
  const titleSize = isMobile ?
    (screenSize === 'xs' ? 'text-base' : 'text-lg') :
    'text-lg';

  return (
    <Card className="w-full max-w-md border-primary/20 transition-all duration-300 hover:shadow-md">
      <CardHeader className="bg-gradient-to-r from-primary/5 to-secondary/5">
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          <CardTitle className={titleSize}>Adaptive Notes Generator</CardTitle>
        </div>
        <CardDescription>
          Create personalized notes based on your learning patterns
        </CardDescription>
      </CardHeader>
      
      <CardContent className={`${cardPadding} space-y-4`}>
        <div className="space-y-2">
          <Label htmlFor="category" className="text-sm font-medium">Category</Label>
          <Select
            value={category}
            onValueChange={setCategory}
          >
            <SelectTrigger id="category" className="w-full">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {categories.map((cat) => (
                <SelectItem key={cat.name} value={cat.name}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="subject" className="text-sm font-medium">Subject</Label>
          <Select
            value={subject}
            onValueChange={setSubject}
            disabled={!category}
          >
            <SelectTrigger id="subject" className="w-full">
              <SelectValue placeholder={category ? "Select subject" : "Select category first"} />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {availableSubjects.map((sub) => (
                <SelectItem key={sub} value={sub}>
                  {sub}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="difficulty" className="text-sm font-medium">Difficulty Level</Label>
          <Select
            value={difficulty}
            onValueChange={setDifficulty}
          >
            <SelectTrigger id="difficulty" className="w-full">
              <SelectValue placeholder="Select difficulty" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="easy">Easy</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="hard">Advanced</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="language" className="text-sm font-medium">Language</Label>
          <LanguageSelector
            currentLanguage={language}
            onLanguageChange={handleLanguageChange}
            variant="outline"
            size="default"
            className="w-full"
          />
        </div>
        
        <div className="border rounded-lg p-4 bg-muted/10 hover:bg-muted/20 transition-colors duration-300">
          <h3 className="font-medium flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-primary" />
            Customization Options
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="visuals" className="flex items-center gap-2 cursor-pointer group">
                <PenTool className="h-3.5 w-3.5 text-primary/70 group-hover:text-primary transition-colors duration-200" />
                <span className="group-hover:text-primary transition-colors duration-200">Include Visual Descriptions</span>
              </Label>
              <Switch 
                id="visuals" 
                checked={includeVisuals}
                onCheckedChange={setIncludeVisuals}
                className="data-[state=checked]:bg-primary"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="examples" className="flex items-center gap-2 cursor-pointer group">
                <BarChart3 className="h-3.5 w-3.5 text-primary/70 group-hover:text-primary transition-colors duration-200" />
                <span className="group-hover:text-primary transition-colors duration-200">Include Examples</span>
              </Label>
              <Switch 
                id="examples" 
                checked={includeExamples}
                onCheckedChange={setIncludeExamples}
                className="data-[state=checked]:bg-primary"
              />
            </div>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className={`${cardPadding} pt-0`}>
        <Button 
          onClick={handleGenerate} 
          className="w-full edaptica-button relative overflow-hidden group"
          disabled={isGenerating || !category || !subject}
        >
          <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-primary/50 via-secondary/50 to-accent/50 opacity-0 group-hover:opacity-20 transition-opacity"></span>
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <span>Generating Adaptive Notes...</span>
            </>
          ) : (
            <>
              <Brain className="mr-2 h-4 w-4" />
              <span>Generate Adaptive Notes</span>
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default AdaptiveNotesGenerator;
